import React, { useState, useEffect, useCallback, useRef } from 'react';
import { disputesAPI } from '../../services/api';
import { showSuccess, showError } from '../../utils/notificationHelper';
import FileViewerModal from '../common/FileViewerModal';
import {
  CalendarIcon,
  MapPinIcon,
  PencilSquareIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const HearingsTabContent = ({ dispute, onRefresh, onScheduleHearing }) => {
  // State for hearings
  const [hearings, setHearings] = useState(dispute?.hearings || []);
  const [hearingsLoading, setHearingsLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false); // Track loading state for parent content
  const [selectedHearing, setSelectedHearing] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);

  // File viewer modal state
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Create a ref to store the getDefaultHearingValues function so it can be accessed by parent components
  const defaultValuesRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const hearingsPerPage = 5;
  const indexOfLastHearing = currentPage * hearingsPerPage;
  const indexOfFirstHearing = indexOfLastHearing - hearingsPerPage;
  const currentHearings = hearings.slice(indexOfFirstHearing, indexOfLastHearing);
  const totalPages = Math.ceil(hearings.length / hearingsPerPage);

  // Form state for rescheduling
  const [rescheduleFormData, setRescheduleFormData] = useState({
    hearing_date: '',
    hearing_time: '',
    location: '',
    notes: '',
    notification_type: 'both',
    notify_complainant: true,
    notify_respondent: true,
    custom_message: ''
  });

  // Form state for attendance
  const [attendanceFormData, setAttendanceFormData] = useState({
    complainant_present: false,
    respondent_present: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // We'll now rely on the parent component for hearings data
  // This eliminates redundant API calls from this child component
  // The parent component fetches data once and distributes it to all child components

  // Create a reference to the onRefresh callback for use in our functions
  const onRefreshRef = useRef(onRefresh);

  // Update the ref when the callback changes
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  // Update local state when hearings are passed from parent
  useEffect(() => {
    if (dispute && dispute.hearings) {
      setHearings(dispute.hearings);
      setCurrentPage(1);
      setContentLoading(false);
      setHearingsLoading(false);
    } else {
      setContentLoading(false);
      setHearingsLoading(false);
    }
  }, [dispute?.hearings]);

  // Helper function to generate default values for a new hearing schedule
  const getDefaultHearingValues = useCallback(() => {
    // Get current date in YYYY-MM-DD format for the date input
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    // Get current time in HH:MM format for the time input
    // Adding 1 hour to current time for reasonable scheduling
    today.setHours(today.getHours() + 1);
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    // Default location - can be customized based on your application's needs
    const defaultLocation = 'Barangay Hall - Conference Room';

    // Generate a summary of the hearing details for the custom message
    const hearingDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(today);

    const hearingTime = `${today.getHours() % 12 || 12}:${minutes} ${today.getHours() >= 12 ? 'PM' : 'AM'}`;

    const customMessage = `You are required to attend a hearing for case ${dispute.case_number} on ${hearingDate} at ${hearingTime} at the ${defaultLocation}. Please arrive 15 minutes early and bring all relevant documents.`;

    return {
      hearing_date: formattedDate,
      hearing_time: formattedTime,
      location: defaultLocation,
      notes: '',
      notification_type: 'both',
      notify_complainant: true,
      notify_respondent: true,
      custom_message: customMessage
    };
  }, [dispute.case_number]);

  // Store the default values function in a ref and expose it to parent components
  useEffect(() => {
    // Store the getDefaultHearingValues function in the ref
    defaultValuesRef.current = getDefaultHearingValues;
  }, [getDefaultHearingValues]);

  // Create an enhanced version of onScheduleHearing that includes default values
  const handleScheduleHearing = useCallback(() => {
    if (onScheduleHearing && typeof onScheduleHearing === 'function') {
      // Get the default values
      const defaultValues = getDefaultHearingValues();
      // Call the original function with default values
      onScheduleHearing(defaultValues);
    }
  }, [onScheduleHearing, getDefaultHearingValues]);

  // Format functions
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Not specified';
    try {
      // Parse time string (HH:MM:SS) to display in 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12; // Convert to 12-hour format
      return `${formattedHour}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString; // Return original if parsing fails
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rescheduled: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-gray-100 text-gray-800'
    };
    return `px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return <CalendarIcon className="h-4 w-4 mr-1.5" />;
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4 mr-1.5" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 mr-1.5" />;
      case 'rescheduled':
        return <ArrowPathIcon className="h-4 w-4 mr-1.5" />;
      case 'pending':
        return <ExclamationCircleIcon className="h-4 w-4 mr-1.5" />;
      default:
        return null;
    }
  };  // Handle reschedule
  const handleReschedule = (hearing) => {
    setSelectedHearing(hearing);
    setRescheduleFormData({
      hearing_date: hearing.hearing_date,
      hearing_time: hearing.hearing_time,
      location: hearing.location || '',
      notes: hearing.notes || '',
      notification_type: 'both',
      notify_complainant: true,
      notify_respondent: true,
      custom_message: `Your hearing for case ${dispute.case_number} has been rescheduled.`
    });
    setShowRescheduleModal(true);
  };

  const handleAttendance = (hearing) => {
    setSelectedHearing(hearing);
    // Ensure boolean values are properly set (not undefined or null)
    setAttendanceFormData({
      complainant_present: hearing.complainant_present === true,
      respondent_present: hearing.respondent_present === true
    });
    setShowAttendanceModal(true);
  };

  const submitReschedule = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      // Log for debugging the notification data being sent
      console.log('Sending reschedule data:', rescheduleFormData);

      const response = await disputesAPI.updateHearing(dispute.id, selectedHearing.id, rescheduleFormData);

      // Show appropriate success message based on notification settings
      if (rescheduleFormData.notification_type !== 'none') {
        const notifyList = [];
        if (rescheduleFormData.notify_complainant) notifyList.push('complainant');
        if (rescheduleFormData.notify_respondent) notifyList.push('respondent');

        if (notifyList.length > 0) {
          const notifyStr = notifyList.join(' and ');
          const notifyMethod =
            rescheduleFormData.notification_type === 'both' ? 'email and SMS' :
            rescheduleFormData.notification_type === 'email' ? 'email' : 'SMS';

          showSuccess(`Hearing rescheduled successfully. Notifications sent to ${notifyStr} via ${notifyMethod}.`);
        } else {
          showSuccess('Hearing rescheduled successfully. No recipients selected for notifications.');
        }
      } else {
        showSuccess('Hearing rescheduled successfully. No notifications sent.');
      }

      setShowRescheduleModal(false);

      // Use the onRefresh callback from parent to update data with a single API call
      // This is more efficient than making separate API calls from each component
      if (onRefreshRef.current) await onRefreshRef.current();
    } catch (error) {
      console.error('Error rescheduling hearing:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      showError('Failed to reschedule hearing: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const submitAttendance = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      // Log the request data before sending
      console.log('Sending attendance update request:', {
        disputeId: dispute.id,
        hearingId: selectedHearing.id,
        data: attendanceFormData
      });

      // Ensure boolean values and use updateAttendance API endpoint
      const formattedData = {
        complainant_present: attendanceFormData.complainant_present === true,
        respondent_present: attendanceFormData.respondent_present === true
      };
      // Make the API call
      const response = await disputesAPI.updateAttendance(dispute.id, selectedHearing.id, formattedData);
      console.log('Attendance update success response:', response.data);

      showSuccess('Attendance updated successfully');
      setShowAttendanceModal(false);

      // Use the onRefresh callback from parent to update data with a single API call
      if (onRefreshRef.current) await onRefreshRef.current();
    } catch (error) {
      console.error('Error updating attendance:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config ? {
          method: error.config.method,
          url: error.config.url,
          baseURL: error.config.baseURL,
          headers: error.config.headers
        } : 'No config available'
      });

      // More user-friendly error message
      let errorMessage = 'Failed to update attendance';
      if (error.response?.data?.error) {
        errorMessage += ': ' + error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }

      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSummons = async (hearingId) => {
    try {
      // Fetch the PDF from API
      const response = await disputesAPI.downloadSummons(dispute.id, hearingId);

      // Ensure we have the correct response type
      console.log('API Response:', {
        status: response.status,
        headers: response.headers,
        dataType: typeof response.data,
        dataLength: response.data?.length || response.data?.byteLength || 'unknown'
      });

      // Create blob URL with explicit PDF type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create simplified file object for FileViewerModal
      const fileObject = {
        id: hearingId,
        filename: `summons-${dispute.case_number}.pdf`,
        file_url: url,
        name: `Summons - Case ${dispute.case_number}`,
        description: `Summons document for hearing on case ${dispute.case_number}`,
        submitted_by: 'System Generated',
        created_at: new Date().toISOString(),
        file_size: blob.size,
        file_type: 'application/pdf', // Explicitly set the type
        mimeType: 'application/pdf' // Additional type property
      };

      setSelectedFile(fileObject);
      setShowFileViewer(true);
    } catch (error) {
      console.error('Error loading summons:', error);
      showError('Failed to load summons document');
    }
  };

  const handleDownloadSummons = async (fileId) => {
    try {
      // Re-fetch the file for download since the blob URL might be revoked
      const response = await disputesAPI.downloadSummons(dispute.id, fileId);

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a link element and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `summons-${dispute.case_number}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

      showSuccess('Summons downloaded successfully');
    } catch (error) {
      console.error('Error downloading summons:', error);
      showError('Failed to download summons');
    }
  };

  return (
    <div className="">
      <div className="flex items-center justify-between mb-6 pb-2 border-b border-gray-100 ">
          <div className="flex items-center">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Scheduled Hearings</h3>
          </div>

          {/* Schedule hearing button for ongoing cases */}
          {dispute.status !== 'settled' && dispute.status !== 'escalated' && (
            <button
              type="button"
              onClick={handleScheduleHearing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      transition-all duration-200 flex items-center"
            >
              <CalendarIcon className="h-5 w-5 mr-2" />
              Schedule Hearing
            </button>
          )}
        </div>

        {contentLoading ? (
          <div className="flex justify-center items-center py-8 w-full">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-3"></div>
              <p className="text-sm text-gray-600"></p>
            </div>
          </div>
        ) : (
        <>
        {hearingsLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : (!hearings || hearings.length === 0) ? (
          <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg bg-gray-50">
            <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No hearings have been scheduled for this case yet</p>
            <p className="text-sm text-gray-400 mt-1">Hearings will appear here once scheduled</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>

                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Summons
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendance
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentHearings.map((hearing, index) => (
                    <tr key={hearing.id || index} className="hover:bg-blue-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 text-gray-500 mr-2" />
                            <div className="text-sm font-medium text-gray-900">{formatDate(hearing.hearing_date)}</div>
                        </div>

                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 text-gray-500 mr-2" />
                          <div className="text-sm text-gray-900">{formatTime(hearing.hearing_time)}</div>
                        </div>


                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 text-gray-500 mr-2" />
                          <div className="text-sm text-gray-900">{hearing.location}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`${getStatusBadgeClass(hearing.status)} flex items-center`}>
                          {getStatusIcon(hearing.status)}
                          {hearing.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm space-y-1.5">
                          <p className={`flex items-center ${hearing.complainant_summons_sent ? 'text-green-700' : 'text-gray-400'}`}>
                            {hearing.complainant_summons_sent ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-green-100 text-green-800">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-gray-100 text-gray-400">-</span>
                            )}
                            <span className="text-gray-800">Complainant</span>
                          </p>
                          <p className={`flex items-center ${hearing.respondent_summons_sent ? 'text-green-700' : 'text-gray-400'}`}>
                            {hearing.respondent_summons_sent ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-green-100 text-green-800">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-gray-100 text-gray-400">-</span>
                            )}
                            <span className="text-gray-800">Respondent</span>
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm space-y-1.5">
                          <p className={`flex items-center ${hearing.complainant_present ? 'text-green-700' : 'text-red-700'}`}>
                            {hearing.complainant_present ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-green-100 text-green-800">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-red-100 text-red-800">✗</span>
                            )}
                            <span className="text-gray-800">Complainant</span>
                          </p>
                          <p className={`flex items-center ${hearing.respondent_present ? 'text-green-700' : 'text-red-700'}`}>
                            {hearing.respondent_present ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-green-100 text-green-800">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 mr-1.5 rounded-full bg-red-100 text-red-800">✗</span>
                            )}
                            <span className="text-gray-800">Respondent</span>
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAttendance(hearing)}
                            className="text-green-600 hover:text-green-800"
                            title="Update Attendance"
                          >
                            <UserCircleIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleReschedule(hearing)}
                            className="text-yellow-600 hover:text-yellow-800"
                            title="Reschedule Hearing"
                          >
                            <ClockIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleViewSummons(hearing.id)}
                            className="text-purple-600 hover:text-purple-800"
                            title="View Summons"
                          >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstHearing + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastHearing, hearings.length)}
                      </span>{' '}
                      of <span className="font-medium">{hearings.length}</span> hearings
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${
                          currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      {[...Array(totalPages).keys()].map((number) => (
                        <button
                          key={number + 1}
                          onClick={() => setCurrentPage(number + 1)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                            currentPage === number + 1
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {number + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${
                          currentPage === totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl animate-modal-appear">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-amber-50 to-white-100 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg text-amber-600">
                    <CalendarIcon className="h-6 w-6 " />
                  </div>
                  <h2 className="text-xl font-semibold">
                    Reschedule Hearing
                  </h2>
                </div>
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="hover:bg-white hover:bg-opacity-20 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all duration-200"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <small className="mt-2 px-2">
                Case No. <span className="font-medium ">{dispute.case_number}</span> • &nbsp;
                {dispute.complainant_name} vs {dispute.respondent_name}
              </small>
            </div>

            <form onSubmit={submitReschedule}>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {/* Left column */}
                <div className="space-y-6">
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center">
                      <CalendarIcon className="h-5 w-5 mr-2 text-amber-600" />
                      Hearing Details
                    </h3>

                    <div className="space-y-4">
                      {/* Hearing Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hearing Date
                        </label>
                        <input
                          type="date"
                          name="hearing_date"
                          value={rescheduleFormData.hearing_date}
                          onChange={(e) => setRescheduleFormData({...rescheduleFormData, hearing_date: e.target.value})}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          required
                        />
                        {errors.hearing_date && <p className="mt-1 text-sm text-red-600">{errors.hearing_date}</p>}
                      </div>

                      {/* Hearing Time */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hearing Time
                        </label>
                        <input
                          type="time"
                          name="hearing_time"
                          value={rescheduleFormData.hearing_time}
                          onChange={(e) => setRescheduleFormData({...rescheduleFormData, hearing_time: e.target.value})}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          required
                        />
                        {errors.hearing_time && <p className="mt-1 text-sm text-red-600">{errors.hearing_time}</p>}
                      </div>

                      {/* Location */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <textarea
                          type="text"
                          name="location"
                          rows={4}
                          value={rescheduleFormData.location}
                          onChange={(e) => setRescheduleFormData({...rescheduleFormData, location: e.target.value})}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          required
                        />
                        {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                    <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2 text-amber-600" />
                      Notes & Notifications
                    </h3>

                    <div className="space-y-4">
                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          value={rescheduleFormData.notes}
                          onChange={(e) => setRescheduleFormData({...rescheduleFormData, notes: e.target.value})}
                          rows={3}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        {errors.notes && <p className="mt-1 text-sm text-red-600">{errors.notes}</p>}
                      </div>

                      {/* Notification Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Send Notifications
                        </label>
                        <select
                          value={rescheduleFormData.notification_type}
                          onChange={(e) => setRescheduleFormData({...rescheduleFormData, notification_type: e.target.value})}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="none">No notification</option>
                          <option value="email">Email only</option>
                          <option value="sms">SMS only</option>
                          <option value="both">Both Email and SMS</option>
                        </select>
                      </div>

                      {rescheduleFormData.notification_type !== 'none' && (
                        <>
                          <div className="grid grid-cols-1 gap-3">
                            <label className={`flex items-center p-2.5 rounded-md cursor-pointer border ${
                              rescheduleFormData.notify_complainant
                                ? 'bg-amber-50 border-amber-300'
                                : 'bg-white border-gray-200'
                            }`}>
                              <input
                                type="checkbox"
                                name="notify_complainant"
                                checked={rescheduleFormData.notify_complainant}
                                onChange={(e) => setRescheduleFormData({...rescheduleFormData, notify_complainant: e.target.checked})}
                                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                              />
                              <div className="ml-3">
                                <span className="text-sm font-medium block leading-none">{dispute.complainant_name || 'Complainant'}</span>
                                <span className="text-xs text-gray-500">Complainant</span>
                              </div>
                            </label>

                            <label className={`flex items-center p-2.5 rounded-md cursor-pointer border ${
                              rescheduleFormData.notify_respondent
                                ? 'bg-amber-50 border-amber-300'
                                : 'bg-white border-gray-200'
                            }`}>
                              <input
                                type="checkbox"
                                name="notify_respondent"
                                checked={rescheduleFormData.notify_respondent}
                                onChange={(e) => setRescheduleFormData({...rescheduleFormData, notify_respondent: e.target.checked})}
                                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                              />
                              <div className="ml-3">
                                <span className="text-sm font-medium block leading-none">{dispute.respondent_name || 'Respondent'}</span>
                                <span className="text-xs text-gray-500">Respondent</span>
                              </div>
                            </label>
                          </div>

                          {/* Custom Message */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Custom Message
                            </label>
                            <textarea
                              name="custom_message"
                              value={rescheduleFormData.custom_message}
                              onChange={(e) => setRescheduleFormData({...rescheduleFormData, custom_message: e.target.value})}
                              rows={5}
                              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              placeholder="Optional message to include in notification"
                            />
                            <div className="mt-1 text-xs text-gray-500 text-right">
                              {rescheduleFormData.custom_message.length}/160
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Controls */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 shadow-sm flex items-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="h-5 w-5 mr-2" />
                      Reschedule
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in-down">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-gradient-to-r from-green-50 to-white-100 px-6 py-4 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg text-green-600">
                      <UserCircleIcon className="h-6 w-6 " />
                    </div>
                    <h2 className="text-xl font-semibold">
                      Update Attendance
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowAttendanceModal(false)}
                    className="hover:bg-white hover:bg-opacity-20 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all duration-200"
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Hearing on {formatDate(selectedHearing?.hearing_date)} at {formatTime(selectedHearing?.hearing_time)}
                </p>
              </div>
              <form onSubmit={submitAttendance}>
                <div className="bg-white px-6 py-5">
                  <div className="space-y-5">
                    <p className="text-sm text-gray-500">
                      Mark which parties were present at this hearing.
                    </p>

                    <div className="grid grid-cols-1 gap-3">
                      <label className={`flex items-center p-4 rounded-md cursor-pointer border ${
                        attendanceFormData.complainant_present
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200'
                      }`}>
                        <input
                          type="checkbox"
                          name="complainant_present"
                          checked={attendanceFormData.complainant_present}
                          onChange={(e) => setAttendanceFormData({...attendanceFormData, complainant_present: e.target.checked})}
                          className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="ml-4">
                          <span className="text-base font-medium block leading-tight">{dispute.complainant_name || 'Complainant'}</span>
                          <span className="text-sm text-gray-500">Complainant</span>
                        </div>
                        {attendanceFormData.complainant_present ? (
                          <CheckCircleIcon className="ml-auto h-6 w-6 text-green-500" />
                        ) : (
                          <XCircleIcon className="ml-auto h-6 w-6 text-red-300" />
                        )}
                      </label>

                      <label className={`flex items-center p-4 rounded-md cursor-pointer border ${
                        attendanceFormData.respondent_present
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200'
                      }`}>
                        <input
                          type="checkbox"
                          name="respondent_present"
                          checked={attendanceFormData.respondent_present}
                          onChange={(e) => setAttendanceFormData({...attendanceFormData, respondent_present: e.target.checked})}
                          className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <div className="ml-4">
                          <span className="text-base font-medium block leading-tight">{dispute.respondent_name || 'Respondent'}</span>
                          <span className="text-sm text-gray-500">Respondent</span>
                        </div>
                        {attendanceFormData.respondent_present ? (
                          <CheckCircleIcon className="ml-auto h-6 w-6 text-green-500" />
                        ) : (
                          <XCircleIcon className="ml-auto h-6 w-6 text-red-300" />
                        )}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowAttendanceModal(false)}
                    className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 shadow-sm flex items-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        Update Attendance
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={showFileViewer}
        onClose={() => {
          setShowFileViewer(false);
          setSelectedFile(null);
          // Clean up blob URL if it exists
          if (selectedFile?.file_url && selectedFile.file_url.startsWith('blob:')) {
            window.URL.revokeObjectURL(selectedFile.file_url);
          }
        }}
        file={selectedFile}
        onDownload={handleDownloadSummons}
      />
      </>
      )}
    </div>
  );
};

export default React.memo(HearingsTabContent);
